import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export { motion, AnimatePresence }

export function FadeIn({ children, delay = 0, y = 0, duration = 0.35, className, style }) {
    return (
        <motion.div
            initial={{ opacity: 0, y }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration, delay, ease: 'easeOut' }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

export function FadeUp({ children, delay = 0, className, style }) {
    return (
        <FadeIn delay={delay} y={12} className={className} style={style}>
            {children}
        </FadeIn>
    )
}

export function StaggerContainer({ children, className, style }) {
    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

export function StaggerItem({ children, className, style }) {
    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
            }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

export function ScaleIn({ children, delay = 0, className, style }) {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25, delay, ease: 'easeOut' }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

export function SlideInRight({ children, className, style }) {
    return (
        <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

export function ModalOverlay({ show, children }) {
    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                    style={{ backdropFilter: 'blur(2px)' }}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 10 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                        {children}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export function ButtonLoader({ loading, children }) {
    return (
        <span className="inline-flex items-center justify-center gap-2">
            {loading && (
                <motion.svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </motion.svg>
            )}
            {children}
        </span>
    )
}

export function Shimmer({ className }) {
    return (
        <div className={`relative overflow-hidden ${className}`}>
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/5 to-transparent animate-[shimmer_1.5s_infinite]" />
        </div>
    )
}

export function CountUp({ value, duration = 1, prefix = '', suffix = '' }) {
    const [display, setDisplay] = React.useState(0)
    const ref = React.useRef(null)
    const [inView, setInView] = React.useState(false)

    React.useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect() } },
            { threshold: 0.3 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    React.useEffect(() => {
        if (!inView) return
        let start = 0
        const end = value
        const step = Math.max(1, Math.floor(end / (duration * 60)))
        const timer = setInterval(() => {
            start += step
            if (start >= end) { setDisplay(end); clearInterval(timer) }
            else setDisplay(start)
        }, 1000 / 60)
        return () => clearInterval(timer)
    }, [inView, value, duration])

    return <span ref={ref}>{prefix}{display.toLocaleString()}{suffix}</span>
}
